import { useEffect, useRef, useState } from 'react';
import { applyAspectRatio, type ResizeHandle } from '@/components/canvas/transform/useResize';

export interface BlockResizeState {
  id: string;
  handle: ResizeHandle;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  startClientX: number;
  startClientY: number;
  minWidth: number;
  minHeight: number;
}

export function useBlockResize(
  zoom: number,
  updateBlockSilent: (
    id: string,
    patch: { layout: { x: number; y: number; width: number; height: number } },
  ) => void,
  commitBlockLayout: (
    id: string,
    layout: { x: number; y: number; width: number; height: number },
  ) => void,
) {
  const [resizeState, setResizeState] = useState<BlockResizeState | null>(null);
  const [resizePreview, setResizePreview] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const resizePreviewRef = useRef(resizePreview);
  resizePreviewRef.current = resizePreview;
  const updateSilentRef = useRef(updateBlockSilent);
  const commitRef = useRef(commitBlockLayout);
  updateSilentRef.current = updateBlockSilent;
  commitRef.current = commitBlockLayout;

  useEffect(() => {
    if (!resizeState) return;
    const {
      id,
      handle,
      startX,
      startY,
      startW,
      startH,
      startClientX,
      startClientY,
      minWidth,
      minHeight,
    } = resizeState;

    const onMove = (e: MouseEvent) => {
      const scale = zoom > 0 ? zoom : 1;
      const dx = (e.clientX - startClientX) / scale;
      const dy = (e.clientY - startClientY) / scale;
      let x = startX;
      let y = startY;
      let w = startW;
      let h = startH;

      if (handle.includes('e')) w = Math.max(minWidth, startW + dx);
      if (handle.includes('w')) {
        w = Math.max(minWidth, startW - dx);
        x = startX + (startW - w);
      }
      if (handle.includes('s')) h = Math.max(minHeight, startH + dy);
      if (handle.includes('n')) {
        h = Math.max(minHeight, startH - dy);
        y = startY + (startH - h);
      }

      if (e.shiftKey) {
        ({ x, y, w, h } = applyAspectRatio(handle, startW, startH, x, y, w, h));
      }

      const preview = { x, y, width: w, height: h };
      setResizePreview(preview);
      updateSilentRef.current(id, { layout: preview });
    };

    const onUp = () => {
      const preview = resizePreviewRef.current;
      if (preview) {
        commitRef.current(id, preview);
      }
      setResizeState(null);
      setResizePreview(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizeState, zoom]);

  return {
    resizeState,
    setResizeState,
    resizePreview,
    setResizePreview,
    resizePreviewRef,
  };
}
